import React, { useMemo, useRef, useState } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import { MultiSelect, Option } from 'react-multi-select-component'
import { useNavigate } from 'react-router-dom'

import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useDeleteUserShareSettingsMutation,
  useGetUserByIdQuery,
  useGetUserShareSettingsQuery,
  useGetAllUsersQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
} from '../../../store/user/user'

import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'

import { useOutsideClick } from '../../../common/useOutsideClick'

import { ShareType } from '../../../models/User'

import { sortUserOptionsBySelected, UserOption, userToUserOption } from './utils'

import './Collaboration.scss'

interface UserShareFormProps {
  shareType: ShareType
  attachedShareTypes?: ShareType[]
  noSelectedUserText: string
  infoText?: string
  selectText?: string
  saveButtonText?: string
  multiSelectWarningText?: string
  hideSaveButton?: boolean
  onSelectedUsersChange?: (userIds: string[]) => void
  userId: string
  showInModal?: boolean
}

interface CheckboxItemRendererProps {
  checked: boolean
  option: UserOption
  disabled?: boolean
  onClick: () => void
}

export const UserShareForm: React.FC<UserShareFormProps> = ({
  shareType,
  attachedShareTypes = [],
  noSelectedUserText,
  infoText,
  selectText,
  saveButtonText,
  multiSelectWarningText,
  hideSaveButton,
  onSelectedUsersChange,
  userId,
  showInModal,
}) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({ accountId, identityTypes: ['idp', 'cross-account'] })
  const { data: userShareSettings } = useGetUserShareSettingsQuery({ accountId, userId, shareType, pageRequest: { page: 0, size: 500 } })

  const [saveUserShareSettings] = useSaveUserShareSettingsMutation()

  const apiSelectedIds = useMemo(() => (userShareSettings?.data ?? []).map((s) => s.sharedWithUserId), [userShareSettings])

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(apiSelectedIds)
  const [lastSyncedIds, setLastSyncedIds] = useState<string[]>(apiSelectedIds)
  const [selectedUserIdsSnapshot, setSelectedUserIdsSnapshot] = useState<string[]>(selectedUserIds)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [shareSettingsUpdateLoading, setShareSettingsUpdateLoading] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setIsOpen(false)
    setSelectedUserIdsSnapshot(selectedUserIds)
  })

  if (lastSyncedIds !== apiSelectedIds) {
    setLastSyncedIds(apiSelectedIds)
    setSelectedUserIds(apiSelectedIds)
    onSelectedUsersChange?.(apiSelectedIds)
  }

  const userOptions = useMemo<UserOption[]>(
    () => (users ? users.data.filter((user) => !!user.email && user.id !== userId).map(userToUserOption) : []),
    [userId, users],
  )

  const sortedUserOptions = useMemo(() => sortUserOptionsBySelected(userOptions, selectedUserIdsSnapshot), [userOptions, selectedUserIdsSnapshot])

  const selectedUserOptions = userOptions.filter((u) => selectedUserIds.includes(u.value))

  const customValueRenderer = (selected: Option[]) => {
    return selected.length ? `${selected.length} user${selected.length > 1 ? 's' : ''} selected` : noSelectedUserText
  }

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (isOpen) {
      return
    }

    setIsOpen(true)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen((prev) => !prev)
  }

  const ArrowRenderer = ({ expanded }: { expanded: boolean }) => (
    <div onClick={handleToggle}>{expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}</div>
  )

  const ClearIcon = <i className='bi bi-x-lg' />

  const handleSaveUserShareSettings = async () => {
    const shareTypes = new Set([shareType, ...attachedShareTypes])

    setShareSettingsUpdateLoading(true)
    try {
      for (const shareType of shareTypes) {
        await saveUserShareSettings({
          accountId,
          userId,
          payload: {
            shareType,
            sharedWithUserIds: selectedUserIds,
          },
        })
      }
      dispatch(addToast({ variant: 'success', title: 'Sharing settings saved', messages: ['Sharing settings saved successfully.'] }))
    } catch (e) {
      console.error(e)
    } finally {
      setShareSettingsUpdateLoading(false)
    }
  }

  const CheckboxItemRenderer = ({ checked, option, onClick, disabled }: CheckboxItemRendererProps) => {
    return (
      <div className={`item-renderer ${disabled ? 'disabled' : ''}`}>
        <Form.Check
          disabled={disabled || false}
          key={option.key}
          type='checkbox'
          id={`option_${option.key}`}
          label={
            <>
              {option.value !== '' ? (
                <>
                  <strong>{option.label}</strong> - <small>{option.email}</small>
                  {option.crossAccount ? <TagElement className='user-share-user-tag ms-2'>CROSS-ACCOUNT</TagElement> : null}
                </>
              ) : (
                option.label
              )}
            </>
          }
          checked={checked}
          onChange={onClick}
          value={option.value}
        />
      </div>
    )
  }

  return (
    <>
      {infoText ? (
        <Row>
          <Col>
            <Alert variant='warning' className='d-flex gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>{infoText}</span>
            </Alert>
          </Col>
        </Row>
      ) : null}
      <Row className={`${showInModal ? 'flex-column' : ''}`}>
        {selectText ? <Col>{selectText}</Col> : null}
        <Col>
          <div ref={containerRef} onClick={handleOpen}>
            <MultiSelect
              isOpen={isOpen}
              hasSelectAll={false}
              options={sortedUserOptions}
              value={selectedUserOptions}
              onChange={(selected: UserOption[]) => {
                const ids = selected.map((u) => u.value)
                setSelectedUserIds(ids)
                onSelectedUsersChange?.(ids)
              }}
              labelledBy='Select'
              isCreatable={true}
              valueRenderer={customValueRenderer}
              ItemRenderer={CheckboxItemRenderer}
              ArrowRenderer={ArrowRenderer}
              ClearSelectedIcon={ClearIcon}
              overrideStrings={{ noOptions: 'You do not have any teammates.', selectAll: 'Select/Deselect All' }}
            />
          </div>
        </Col>
        {multiSelectWarningText && !usersFetching && selectedUserOptions.length === 0 && (
          <Col className='d-flex gap-1 align-items-center'>
            <i className='bi bi-exclamation-triangle zocks-text-warning-text' />
            <span className='multi-select-warning-text'>{multiSelectWarningText}</span>
          </Col>
        )}
        {showInModal && selectedUserOptions.length > 0 ? (
          <Col className='d-flex flex-wrap gap-2 mt-2'>
            {selectedUserOptions.map((userOption) => (
              <span key={userOption.value} className='d-flex align-items-center gap-2 rounded-1 p-2 zocks-bg-color-gray-200 zocks-text-gray-900'>
                {userOption.label}
                <i
                  className='bi bi-x cursor-pointer'
                  role='button'
                  onClick={() => {
                    const updatedIds = selectedUserIds.filter((id) => id !== userOption.value)
                    setSelectedUserIds(updatedIds)
                    onSelectedUsersChange?.(updatedIds)
                  }}
                />
              </span>
            ))}
          </Col>
        ) : null}
      </Row>

      {!hideSaveButton ? (
        <Row>
          <Col>
            <div className='d-flex justify-content-sm-center justify-content-md-end'>
              <Button className='p-2' variant='primary' onClick={handleSaveUserShareSettings} loading={shareSettingsUpdateLoading}>
                {saveButtonText}
              </Button>
            </div>
          </Col>
        </Row>
      ) : null}
    </>
  )
}

export const CollaborationTab: React.FC = () => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { hasQuota } = useZocksQuota()
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')

  const { data: userData, isFetching: userLoading } = useGetUserByIdQuery(
    { accountId, userId: currentUserId },
    { refetchOnMountOrArgChange: true, skip: !sessionSharingAvailable },
  )

  const [saveUserById, { isLoading: isUserUpdating }] = useSaveUserByIdMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()
  const [userSharingEnabledOverwrite, setUserSharingEnabledOverwrite] = useState<boolean | null>(null)

  const handleUpgradePlanClick = () => {
    navigate('/account/plan/plan')
  }

  if (userLoading) {
    return <div className='skeleton' style={{ width: '100%', height: '24px' }} />
  }

  if (!sessionSharingAvailable) {
    return (
      <Container className='p-0'>
        <Row className='zocks-mb-2.5 collaboration-disabled-container gap-5'>
          <Col xs={12} lg>
            <div className='d-flex flex-column gap-3'>
              <h5>Upgrade to unlock this feature</h5>
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-check-circle-fill' />
                  <div className='label-medium'>Collaborate with your team members</div>
                </div>
                <div className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-check-circle-fill' />
                  <div className='label-medium'>Control who can discover your meetings</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} lg className='d-flex flex-column justify-content-center align-items-lg-end'>
            <div className='d-flex'>
              <Button variant='primary' onClick={handleUpgradePlanClick}>
                Upgrade plan <img alt='Upgrade' src={PrizeBadge} />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    )
  }

  const handleSessionSharingEnabledChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userData) {
      return
    }
    const currentUserData = userData.data
    saveUserById({
      accountId,
      userId: currentUserId,
      userData: { settings: { ...currentUserData.settings, sessionSharingEnabled: e.currentTarget.checked, contactSharingEnabled: e.currentTarget.checked } },
    })
    setUserSharingEnabledOverwrite(e.currentTarget.checked)
    if (!e.currentTarget.checked) {
      deleteUserShareSettings({ accountId, userId: currentUserId, shareType: 'meeting' })
      deleteUserShareSettings({ accountId, userId: currentUserId, shareType: 'contact' })
    }
  }

  const isEnterpriseEnforced: boolean =
    userData?.data?.enterpriseSettings?.sessionSharingEnabled != null || userData?.data?.enterpriseSettings?.contactSharingEnabled != null
  const isDisabled = userLoading || isUserUpdating || isEnterpriseEnforced
  const isSwitchChecked =
    userSharingEnabledOverwrite ??
    (userData?.data?.effectiveSettings?.sessionSharingEnabled && userData?.data?.effectiveSettings?.contactSharingEnabled) ??
    false

  const renderEnterpriseEnforcedInfoBox = () => {
    return (
      <Alert variant='warning' className='d-flex gap-3 align-items-center'>
        <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
        <span className='zocks-text-gray-900'>Managed at account or enterprise level.</span>
      </Alert>
    )
  }

  return (
    <Container className='d-flex flex-column gap-5 p-0'>
      <Row>
        <Col>
          <h5>Share meetings and contacts with your team members</h5>
        </Col>
      </Row>
      <Row className='gap-3 p-0 align-items-center'>
        <Col>
          <ScopedBlock
            scope={{
              scope: 'user:update',
              superScope: ['user:admin'],
              resource: `user:${currentUserId}`,
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            {isEnterpriseEnforced && renderEnterpriseEnforcedInfoBox()}
            <Row>
              <Col className='paragraph-medium'>I want to share my meetings and contacts with my team members</Col>
              <Col>
                <Form.Switch disabled={isDisabled} checked={isSwitchChecked} onChange={(e) => handleSessionSharingEnabledChanged(e)} />
              </Col>
            </Row>
          </ScopedBlock>
        </Col>
      </Row>
      {isSwitchChecked && !isEnterpriseEnforced && (
        <UserShareForm
          shareType={'meeting'}
          attachedShareTypes={['contact']}
          noSelectedUserText={'All users can discover your meetings and contacts'}
          infoText={
            'You can check each user to grant individual access to your meetings and contacts. If all checkboxes remain unchecked, they will be shared with all teammates.'
          }
          selectText={'Specify access to your meetings and contacts for selected users.'}
          saveButtonText={'Save meeting and contact share settings'}
          userId={currentUserId}
        />
      )}
    </Container>
  )
}
